import React, { useContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import Layout from '../templates/Layout/Layout';
import { LoadingSpinner } from '@rabbit/elements/shared-components';
import ROUTE_NAME from './url-constants';
import ModalTermsConditions from '../components/molecules/ModalTermsConditions/ModalTermsConditions';
import { FileStorageProviderWrapper } from '@rabbit/bizproc/react';
import { useTranslation } from 'react-i18next';
import { useMyConsumerPersona } from '@rabbit/data/portal';
import { getProfileCompletionStatus } from './profileCompletionStatus';
import { AppContext } from '@rabbit/app-context';
import { ProfileCompletionModal } from '../components/organisms/ProfileCompletionModal/ProfileCompletionModal';

const PrivateRoutes = (props: any) => {
  const contextValues = useContext(UserContext);
  const { config } = useContext(AppContext);
  const { t } = useTranslation();
  const currentLocation = useLocation();
  const consumerPersona = useMyConsumerPersona();
  const consumerId = consumerPersona?.personaId;
  const consumerData = consumerPersona?.data?.consumer_private;
  const tenantLink = t('tenantLink');
  const {
    phoneMissing,
    ageMissing,
    termsPending,
    shouldPromptProfile: showProfileCompletion,
  } = getProfileCompletionStatus({
    consumerPrivate: consumerData,
    config,
    tenantLink,
  });

  const NLIC_StopGetIdentity = window.localStorage.getItem(
    'NLIC_StopGetIdentity'
  );
  if (Number(NLIC_StopGetIdentity) < Date.now() / 1000) {
    window.localStorage.removeItem('NLIC_StopGetIdentity');
  }

  // Check if user is in the middle of a passwordless sign-in
  if (window.localStorage.getItem('emailForSignIn')) {
    return (
      <LoadingSpinner
        size="lg"
        isFullScreen
        label={t('Click the button in your latest email to log in.')}
        action={{
          actionFn: () => {
            window.localStorage.removeItem('emailForSignIn');
            window.location.reload();
          },
          actionLabel: 'Stuck? Click here to return to Login.',
        }}
      />
    );
  }

  if (!contextValues?.user && currentLocation.pathname !== ROUTE_NAME.AUTH) {
    return <Navigate to={ROUTE_NAME.AUTH} />;
  }

  const providerId =
    contextValues?.user?.providerId ||
    contextValues?.user?.providerData[0]?.providerId;

  /*
   * ProviderId === 'password' -> for email login
   * ProviderId === 'google.com' || 'apple.com' -> for social login
   */
  if (currentLocation.pathname !== ROUTE_NAME.COMPLETE_PROFILE) {
    const user = contextValues?.user;
    const isEmpty = (s: string) => !s || s.trim().length === 0;
    if (
      providerId !== 'google.com' &&
      providerId !== 'apple.com' &&
      providerId !== 'firebase' &&
      user &&
      isEmpty(user.displayName)
    ) {
      return <Navigate to={ROUTE_NAME.COMPLETE_PROFILE} />;
    }

    if (providerId === 'firebase' && isEmpty(user?.displayName)) {
      return <Navigate to={ROUTE_NAME.COMPLETE_PROFILE} />;
    }
  }

  if (
    window.localStorage.getItem('LRP_VendableId') &&
    !String(currentLocation.pathname).includes(ROUTE_NAME.PRODUCTS_PREVIEW)
  ) {
    const LRP_VendableId = window.localStorage.getItem('LRP_VendableId');
    return (
      <Navigate
        to={ROUTE_NAME.PRODUCTS_PREVIEW + '/' + LRP_VendableId + '/register'}
      />
    );
  }

  //Not to show Navbar and container of max 1024px
  if (currentLocation.pathname === ROUTE_NAME.PRODUCTS_SCAN) {
    return <Outlet />;
  }

  // Handle stored redirect URL, but NOT if we're on complete-profile
  // (user needs to finish profile first before being redirected)
  const redirectUrl = localStorage.getItem('redirectUrl');
  if (
    redirectUrl &&
    !String(currentLocation.pathname).includes(redirectUrl) &&
    currentLocation.pathname !== ROUTE_NAME.COMPLETE_PROFILE
  ) {
    localStorage.removeItem('redirectUrl');
    window.location.href = window.location.origin + redirectUrl;
    return <></>;
  }

  const shouldRenderProfileModal =
    showProfileCompletion && Boolean(consumerId) && !termsPending;

  return contextValues?.user ? (
    <FileStorageProviderWrapper>
      <ModalTermsConditions />
      {shouldRenderProfileModal && consumerId && (
        <ProfileCompletionModal
          consumerId={consumerId as string}
          phoneMissing={phoneMissing}
          ageMissing={ageMissing}
        />
      )}
      <Layout>
        <Outlet />
      </Layout>
    </FileStorageProviderWrapper>
  ) : (
    <Outlet />
  );
};

export default PrivateRoutes;
